import type { FileRevisionResponseDatagramBuf } from "~/classes/generated/response"

import { DatagramBuf } from "~/classes/generated/datagram"
import { FileRevisionRequestDatagramBuf, RequestDatagramBuf } from "~/classes/generated/request"
import { DatagramBufferBuilder } from "~/utils/flatbuffers/builder"
import { addRequestIdToBuf, parseDatagramError, parseRequestId } from "~/utils/flatbuffers/datagram"
import { addEntityPropertySelectionToBuf, addEntityPropertyTimestampToBuf } from "~/utils/flatbuffers/entity"
import { parseFile } from "~/utils/flatbuffers/file"
import { addFileIdToBuf, addFilePropertyIdToBuf, parseFileId } from "~/utils/flatbuffers/id"
import { maybe } from "~/utils/helpers"

const _console = useLogger("file/revision")

export function sendFileRevisionRequests(requests: FileRevisionRequestDatagram[]) {
  const { sendWorkerDatagramBuf } = useCache()
  const builder = new DatagramBufferBuilder(64)
  const requestVector = []
  for (const request of requests) {
    const fileId = addFileIdToBuf(builder, request.fileId)
    const requestId = addRequestIdToBuf(builder, request.requestId)
    const selection = addEntityPropertySelectionToBuf(builder, request.selection)
    const timestamp = addEntityPropertyTimestampToBuf(builder, request.timestamp)
    let properties
    if (request.properties) {
      const parts = request.properties.map(p => addFilePropertyIdToBuf(builder, p))
      properties = FileRevisionRequestDatagramBuf.createPropertiesVector(builder, parts)
    }

    FileRevisionRequestDatagramBuf.startFileRevisionRequestDatagramBuf(builder)
    FileRevisionRequestDatagramBuf.addRequestId(builder, requestId)
    FileRevisionRequestDatagramBuf.addFileId(builder, fileId)
    FileRevisionRequestDatagramBuf.addSelection(builder, selection)
    FileRevisionRequestDatagramBuf.addTimestamp(builder, timestamp)
    FileRevisionRequestDatagramBuf.addOffline(builder, request.offline)
    if (properties)
      FileRevisionRequestDatagramBuf.addProperties(builder, properties)

    const revisionRequest = FileRevisionRequestDatagramBuf.endFileRevisionRequestDatagramBuf(builder)
    RequestDatagramBuf.startRequestDatagramBuf(builder)
    RequestDatagramBuf.addFileRevisionRequest(builder, revisionRequest)
    requestVector.push(RequestDatagramBuf.endRequestDatagramBuf(builder))
  }

  const vector = DatagramBuf.createRequestVector(builder, requestVector)
  DatagramBuf.startDatagramBuf(builder)
  DatagramBuf.addRequest(builder, vector)
  builder.finish(DatagramBuf.endDatagramBuf(builder))
  sendWorkerDatagramBuf(builder.asUint8Array())
}

/** Parse a Flatbuffers representation of a response */
export function handleFileRevisionResponseDatagramBuf(datagram: FileRevisionResponseDatagramBuf) {
  onFileRevisionResponse({
    requestId: parseRequestId(datagram.requestId()!),
    error: maybe(parseDatagramError)(datagram.error()),
    file: maybe(parseFile)(datagram.file()),
    offline: datagram.offline(),
    fileId: parseFileId(datagram.fileId()!),
  })
}

export function onFileRevisionResponse(response: FileRevisionResponseDatagram) {
  const { emitInterfaceEvent } = useEvents()
  const { emitEntityEvent } = useEvents()

  if (response.error)
    return onFileRevisionErrored(response.error)
  if (!response.file)
    throw new Error("No properties in revision response")

  const fileId = response.fileId
  const properties = Object.values(response.file.properties) as FileProperty[]
  emitEntityEvent("fileHistory", fileId, { fileId, properties }, response.offline)
  emitInterfaceEvent("fileRevisionResponse", response, response.requestId)
}

export function onFileRevisionErrored(error: DatagramError) {
  // const { notifyDatagramError } = useAlerts()

  console.error("File revision error", error)

  // Only notify the UI once you feel comfortable that there aren't spurious errors
  // return notifyDatagramError(error, "File revision retrieval was unsuccessful")
}
